import Alpine from 'alpinejs';
import { isEmpty, trim, debounce } from 'lodash-es';
import axios from 'axios';
import aa from 'search-insights';

// Searchanise (검색 결과 페이지용)
const SEARCHANISE_API_KEY = '3Z2v8B4Q0d';
const SEARCHANISE_BASE_URL = 'https://searchserverapi.com';

// Algolia 설정은 window.algoliaShopify.config에서 가져옴
let algoliaSearchClient = null;
let algoliaIndexName = null;

/**
 * Initialize Algolia Client
 */
const initAlgoliaClient = async () => {
  if (algoliaSearchClient && algoliaIndexName) {
    return { searchClient: algoliaSearchClient, indexName: algoliaIndexName };
  }

  try {
    const config = window.algoliaShopify?.config;
    if (!config) {
      throw new Error('Algolia config not available');
    }

    // Import Algolia client
    const { liteClient } = await import('algoliasearch/lite');

    const appId = config.app_id;
    const apiKey = config.search_api_key || config.api_key;

    // 로케일에 따라 인덱스 선택
    const locale = window.Shopify?.locale || '';
    algoliaIndexName = locale === 'en' ? `${config.index_prefix}products__en` : `${config.index_prefix}products`;

    algoliaSearchClient = liteClient(appId, apiKey);

    console.log('Algolia initialized:', { appId, indexName: algoliaIndexName, locale });

    return { searchClient: algoliaSearchClient, indexName: algoliaIndexName };
  } catch (error) {
    console.error('Failed to initialize Algolia client:', error);
    return null;
  }
};

/**
 * Fetch product card HTML using Section Rendering API
 * @see https://shopify.dev/docs/api/ajax/section-rendering
 */
const fetchProductCard = async (handle) => {
  try {
    const locale = window.Shopify?.locale || '';
    const root = window.Shopify?.routes?.root || '/';

    // Section Rendering API를 사용하여 autocomplete-product-card 섹션 렌더링
    // 로케일이 'en'이면 /{locale}/products/{handle}?sections=autocomplete-product-card
    // 그 외에는 /products/{handle}?sections=autocomplete-product-card
    const url =
      locale === 'en'
        ? `/${locale}/products/${handle}?sections=autocomplete-product-card`
        : `/products/${handle}?sections=autocomplete-product-card`;

    const response = await axios.get(url);

    if (response.status === 200 && response.data) {
      // Section Rendering API는 JSON 객체를 반환
      // { "autocomplete-product-card": "<html>..." }
      const sectionHtml = response.data['autocomplete-product-card'];

      if (sectionHtml) {
        return {
          handle,
          html: sectionHtml,
        };
      }
    }

    return { handle, html: null };
  } catch (error) {
    console.error(`Failed to fetch product card for ${handle}:`, error);
    return { handle, html: null };
  }
};

/**
 * Get query parameter from URL
 */
const getQueryParam = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('query') || params.get('q');
};

/**
 * Initialize Tari Search
 */
export const initTariSearch = () => {
  // Global search store
  Alpine.store('tariSearch', {
    // State
    isOpen: false,
    isSearching: false,
    query: '',
    lastSearchQuery: '', // 마지막 검색어 저장
    results: [],
    suggestions: [],
    totalItems: 0,
    hasError: false,
    lastQueryID: null, // Algolia queryID (CTR 트래킹용)
    lastHits: [], // Algolia hits (CTR 트래킹용)

    // Methods
    async search(query) {
      if (!query || query.length < 1) {
        this.reset();
        return;
      }

      // 중복 검색 방지: 이전 검색어와 같으면 스킵
      if (this.lastSearchQuery === query && this.results.length > 0) {
        console.log('Skipping duplicate search for:', query);
        return;
      }

      this.isSearching = true;
      this.hasError = false;
      this.query = query;
      this.lastSearchQuery = query; // 검색어 저장

      try {
        // Algolia 클라이언트 초기화
        const algolia = await initAlgoliaClient();
        if (!algolia || !algolia.searchClient) {
          throw new Error('Algolia not initialized');
        }

        // Algolia 검색 실행 - searchForHits 메서드 사용
        const { results } = await algolia.searchClient.search([
          {
            indexName: algolia.indexName,
            query: query,
            hitsPerPage: 8,
            clickAnalytics: true, // queryID 반환 활성화 (CTR 트래킹용)
            attributesToRetrieve: ['handle', 'title', 'product_type', 'vendor'],
            optionalFilters: ['inventory_available:true<score=100>'],
          },
        ]);

        if (results && results.length > 0 && results[0]) {
          // queryID와 hits 저장 (CTR 트래킹용)
          this.lastQueryID = results[0].queryID || null;
          this.lastHits = results[0].hits || [];
          await this.processResults(results[0]);

          const nbHits = results[0].nbHits || 0;
          // Amplitude: 검색 실행
          window.amplitude?.track('Search Submitted', {
            query,
            results_count: nbHits,
            source: 'autocomplete',
          });
          // Amplitude: 결과 없음
          if (nbHits === 0) {
            window.amplitude?.track('Search No Results', { query, source: 'autocomplete' });
          }
        } else {
          this.results = [];
          this.totalItems = 0;
          this.lastQueryID = null;
          this.lastHits = [];
          window.amplitude?.track('Search Submitted', { query, results_count: 0, source: 'autocomplete' });
          window.amplitude?.track('Search No Results', { query, source: 'autocomplete' });
        }
      } catch (error) {
        console.error('Search error:', error);
        this.hasError = true;
        this.results = [];
        this.suggestions = [];
      } finally {
        this.isSearching = false;
      }
    },

    async processResults(data) {
      // Fetch Query Suggestions first (동시 실행)
      const suggestionsPromise = this.fetchSuggestions(this.query);

      // Process product items
      if (data.hits && data.hits.length > 0) {
        const productHandles = data.hits.map((hit) => hit.handle).filter(Boolean);

        // 결과를 먼저 초기화하고 totalItems 설정 (전체 길이만큼 null로 채움)
        this.results = new Array(productHandles.length).fill(null);
        this.totalItems = data.nbHits || 0;

        // 각 카드를 병렬로 요청하되, 완료되는 즉시 results에 추가
        productHandles.forEach((handle, index) => {
          fetchProductCard(handle)
            .then((response) => {
              if (response && response.html) {
                // 원래 순서대로 삽입하고 Alpine.js 반응성 트리거
                this.results[index] = response.html;
                // 배열 재할당으로 반응성 트리거 (전체 배열 유지, filter 제거)
                this.results = [...this.results];
              }
            })
            .catch((error) => {
              console.error(`Failed to load product card for ${handle}:`, error);
              // 에러 발생 시 null 유지 (자리는 유지)
              this.results[index] = null;
            });
        });
      } else {
        this.results = [];
        this.totalItems = 0;
      }

      // Wait for suggestions to complete
      await suggestionsPromise;
    },

    async fetchSuggestions(query) {
      if (!query || query.length < 2) {
        this.suggestions = [];
        return;
      }

      try {
        const algolia = await initAlgoliaClient();
        if (!algolia || !algolia.searchClient) {
          this.suggestions = [];
          return;
        }

        // Query Suggestions 인덱스 이름 (언어 구분 없이 단일 인덱스 사용)
        const config = window.algoliaShopify?.config;
        const suggestionsIndexName = `${config.index_prefix}products_query_suggestions`;

        // Query Suggestions 검색
        const { results } = await algolia.searchClient.search([
          {
            indexName: suggestionsIndexName,
            query: query,
            hitsPerPage: 6,
            attributesToRetrieve: ['query'],
          },
        ]);

        if (results && results[0] && results[0].hits && results[0].hits.length > 0) {
          // 검색어만 추출
          const extractedSuggestions = results[0].hits.map((hit) => hit.query).filter((q) => q && q.trim().length > 0);

          // Alpine.js 반응성을 위해 새 배열로 할당
          this.suggestions = [...extractedSuggestions];
        } else {
          this.suggestions = [];
        }
      } catch (error) {
        console.error('Failed to fetch suggestions:', error);
        this.suggestions = [];
      }
    },

    // Algolia 클릭 이벤트 전송 (CTR 트래킹)
    sendClickEvent(index) {
      if (!this.lastQueryID || !this.lastHits[index]) return;
      const hit = this.lastHits[index];
      const algoliaConfig = window.algoliaShopify?.config;
      if (!algoliaConfig) return;

      const locale = window.Shopify?.locale || '';
      const idxName = locale === 'en' ? `${algoliaConfig.index_prefix}products__en` : `${algoliaConfig.index_prefix}products`;

      aa('clickedObjectIDsAfterSearch', {
        index: idxName,
        eventName: 'Product Clicked',
        queryID: this.lastQueryID,
        objectIDs: [hit.objectID],
        positions: [index + 1],
      });

      // Amplitude: 검색 결과 클릭
      window.amplitude?.track('Search Result Clicked', {
        query: this.query,
        product_handle: hit.handle,
        position: index + 1,
        source: 'autocomplete',
      });
    },

    open() {
      this.isOpen = true;
      document.body.style.overflow = 'hidden';
      window.amplitude?.track('Search Viewed');
    },

    close() {
      this.isOpen = false;
      document.body.style.overflow = '';
    },

    reset() {
      this.results = [];
      this.suggestions = [];
      this.totalItems = 0;
      this.hasError = false;
      this.lastSearchQuery = ''; // 마지막 검색어도 초기화
    },

    viewAllResults() {
      if (this.query) {
        window.location.href = `${window.Shopify.routes.root}pages/search?type=product&query=${encodeURIComponent(
          this.query,
        )}`;
      }
    },

    init() {
      // Initialize with URL query if exists
      const urlQuery = getQueryParam();
      if (urlQuery) {
        this.query = urlQuery;
      }
    },
  });

  // Search page component
  Alpine.data('tariSearchPage', () => ({
    // State
    isLoading: false,
    isLoadingMore: false,
    query: '',
    results: [],
    sort: 'relevance-desc',
    startIndex: 0,
    itemsPerPage: 12,
    totalItems: 0,
    hasMore: false,

    // Computed
    get isEmpty() {
      return this.results.length === 0 && !this.isLoading;
    },

    // Methods
    async init() {
      const query = getQueryParam();

      if (query) {
        this.query = query;
        await this.search();
      }

      // Watch results and auto-render
      this.$watch('results', () => {
        this.renderResults();
      });
    },

    async search(reset = true) {
      if (!this.query || this.isLoading) return;

      if (reset) {
        this.startIndex = 0;
        this.results = [];
        this.isLoading = true;
      } else {
        this.isLoadingMore = true;
      }

      try {
        const [sortBy, sortOrder] = this.sort.split('-');

        const response = await axios.get(`${SEARCHANISE_BASE_URL}/getresults`, {
          params: {
            api_key: SEARCHANISE_API_KEY,
            q: this.query,
            startIndex: this.startIndex,
            sortBy,
            sortOrder,
            suggestions: true,
            items: true,
            categories: false,
            facets: true,
            maxResults: this.itemsPerPage,
          },
        });

        if (response.status === 200 && response.data) {
          await this.processSearchResults(response.data);
        }
      } catch (error) {
        console.error('Search page error:', error);
      } finally {
        this.isLoading = false;
        this.isLoadingMore = false;
      }
    },

    async processSearchResults(data) {
      this.totalItems = data.totalItems || 0;

      if (data.items && data.items.length > 0) {
        const productHandles = data.items.map((item) => item.link.replace('/collections/', ''));

        const requests = productHandles.map((handle) => fetchProductCard(handle));
        const responses = await Promise.all(requests);

        const newResults = responses.filter((item) => item.html).map((item) => item.html);

        this.results = [...this.results, ...newResults];

        // Update hasMore flag
        const nextIndex = this.startIndex + this.itemsPerPage;
        this.hasMore = nextIndex < this.totalItems;
      } else {
        this.hasMore = false;
      }
    },

    async loadMore() {
      if (!this.hasMore || this.isLoadingMore) return;

      this.startIndex += this.itemsPerPage;
      await this.search(false);
    },

    async changeSort(newSort) {
      if (this.sort === newSort) return;

      this.sort = newSort;
      await this.search(true);
    },

    renderResults() {
      const container = document.getElementById('searchResults');
      if (container) {
        container.innerHTML = this.results.join('');
      }
    },
  }));

  // 디버깅 헬퍼 - 브라우저 콘솔에서 window.debugTariSearch() 실행
  if (typeof window !== 'undefined') {
    window.debugTariSearch = () => {
      const store = Alpine.store('tariSearch');
      console.log('=== Tari Search Debug Info ===');
      console.log('Query:', store.query);
      console.log('Is Searching:', store.isSearching);
      console.log('Has Error:', store.hasError);
      console.log('Suggestions:', store.suggestions);
      console.log('Suggestions Length:', store.suggestions.length);
      console.log('Results:', store.results.length, 'items');
      console.log('Total Items:', store.totalItems);
      console.log('==============================');
      return store;
    };
  }
};
